import React, {useEffect, useRef, useState} from "react";
import ReactMarkdown from 'react-markdown';
import MessageForm from "../MessageForm/MessageForm";
import {Box, Paper, Typography} from "@mui/material";
import remarkGfm from 'remark-gfm';
import MarkdownComponents from "./MarkdownComponents";
import ApiClient from "../../service_clients/ApiClient";
import ThinkingIndicator from "../ThinkingIndicator/ThinkingIndicator";
import Constants from "../../utils/Constants";
import ProductsGrid from "../ProductsGrid/ProductsGrid";
import {v4 as uuidv4} from 'uuid';
import ToMessage from "./ToMessage";
import {EntryPoint as EntryPoint} from "./EntryPoint";
import {Events} from "../../utils/Events";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from "prop-types";
import 'three-dots/dist/three-dots.css';
import GetPageContent from "../../utils/GetPageContent";
import NobiEventTracker from "../../utils/NobiEventTracker";
import Button from "@mui/material/Button";

import styles from "./Chatbot.module.css";


/**
 * This is the primary component for showing the chatbot in the search results drawer
 *
 * @param merchant - object The merchant object
 * @param initialUserMessage - string The initial query to send to the chatbot
 * @param initialAssistantMessage - string The initial message from the assistant
 * @param brandTheme - object A dictionary of brand theme attributes
 * @param onClose - function Callback for closing the chatbot, only used in specific circumstances
 * @param entryPoint - string The entry point for the chatbot
 * @param onSendMessage - function Callback for sending messages to the parent component
 * @param initialCategories - array Any initial categories to show in the chatbot's first message
 * @param assistantConfiguration - object The assistant configuration
 * @param cartProductVariantInternalId {string} The internal ID of the product variant in the cart
 * @param showMessageForm {boolean} Whether to show the message form or not
 * @param showCommentary {boolean} Whether to show the commentary or not
 * @returns {Element} The Chatbot component
 */
export default function Chatbot({
    merchant,
    initialUserMessage,
    initialAssistantMessage,
    brandTheme,
    onClose,
    entryPoint,
    onSendMessage,
    initialCategories,
    assistantConfiguration,
    cartProductVariantInternalId,
    showMessageForm,
    showCommentary
}) {

    // Constants
    const UserRole = "user";
    const AssistantRole = "assistant";
    const NewSearchPrompt = "Hi there, I’m your AI shopping assistant, let me know how I can help! Take a look at our best sellers to get you started.";
    const ChatIconOpacityAsHex = "0D"; // 5% opacity

    const chatRef = useRef(null);
    const lastMessageRef = useRef(null);
    const productGridRef = useRef(null);

    const [messages, setMessages] = useState([]);
    const [disableMessageSending, setDisableMessageSending] = useState(false);
    const [waitingForFirstToken, setWaitingForFirstToken] = useState(false);
    const [displayedProductCategories, setDisplayedProductCategories] = useState(initialCategories);
    const [conversationId, setConversationId] = useState(uuidv4());
    const [lastStructuredFilterPromptResponse, setLastStructuredFilterPromptResponse] = useState(null);
    const [currentStatus, setCurrentStatus] = useState("Thinking");

    useEffect(() => {
        saveConversationId();
    }, [conversationId]);

    /**
     * Makes a request to the API's endpoint if there is an initial message, which will then stream back the results
     * and a list of products
     */
    useEffect(() => {
        if (initialUserMessage && initialAssistantMessage) {
            let initialAssistantMessageObject = ToMessage(initialAssistantMessage, AssistantRole);
            initialAssistantMessageObject.productCategories = initialCategories;

            setMessages([
                initialAssistantMessageObject,
                ToMessage(initialUserMessage, UserRole)
            ]);
        }
        else if (initialUserMessage) {
            let userMessage = ToMessage(initialUserMessage, UserRole);
            if (entryPoint === EntryPoint.SearchBar) {
                userMessage.showSearchWithoutAiButton = true;
            }
            setMessages([userMessage]);
        }
        else if (initialAssistantMessage) {
            let initialAssistantMessageObject = ToMessage(initialAssistantMessage, AssistantRole);
            initialAssistantMessageObject.productCategories = initialCategories;
            setMessages([initialAssistantMessageObject]);
        }
        else if (initialCategories || (showCommentary !== undefined && showCommentary !== null && showCommentary)) {
            // This happens when the cart assistant sends back products but we are still waiting for the commentary
            let initialAssistantMessageObject = ToMessage("", AssistantRole);
            initialAssistantMessageObject.productCategories = initialCategories;
            setMessages([initialAssistantMessageObject]);
        }
        else {
            // Currently this branch of code should never get executed since we removed the "New Conversation" button, but we'll keep it here for now
            prepareNewConversation(null);
        }
    }, [initialUserMessage, initialAssistantMessage]);

    /**
     * This effect is run when the messages are changed and is responsible for scrolling the chat interface to the
     * bottom (most recent message) and for sending the message to the API if the most recent message was from the assistant
     */
    useEffect(() => {
        // Return if no messages or if the first message is from the assistant
        if (!messages || messages?.length === 0 || !messages[messages?.length - 1] || messages[messages?.length - 1].role !== UserRole) {
            return;
        }

        // Ensure chat interface is scrolled to the bottom
        setTimeout(() => {
            scrollToLastMessage();
        }, 100);

        fetchResponseFromApi();
    }, [chatRef, messages]);

    const saveConversationId = () => {
        let nobiData = localStorage.getItem(Constants.MasterLocalStorageKey);
        nobiData = nobiData ? JSON.parse(nobiData) : {};
        nobiData[Constants.KeyForConversationIdInLocalStorage] = conversationId;
        localStorage.setItem(Constants.MasterLocalStorageKey, JSON.stringify(nobiData));
    }

    /**
     * We only search immediately when the user has entered a new search query from tying into a customer's search bar
     *
     * @returns {boolean} Whether we should search immediately for the latest query
     */
    const shouldSearchImmediatelyForLatestQuery = () => {
        return messages.length <= 2
            && messages[messages.length - 1].role === UserRole
            && entryPoint === EntryPoint.SearchBar
            && messages[messages.length - 1].content === initialUserMessage;
    }

    /**
     * This method sends the conversation to the API to get the next message from the assistant
     */
    const fetchResponseFromApi = () => {
        setDisableMessageSending(true);
        setWaitingForFirstToken(true);
        setCurrentStatus("Thinking");

        if (shouldSearchImmediatelyForLatestQuery()) {
            sendSearchRequest();
        }
        else {
            sendGenericMessage();
        }

        // Here we set an empty message so that it can act as the anchor for the thinking indicator and the products grid, we need to do this last so we don't screw up event tracking
        let newMessage = ToMessage("", AssistantRole);
        setMessages(prevMessages => [
            ...prevMessages,
            newMessage
        ]);
    }

    const sendGenericMessage = () => {
        let pageContents = GetPageContent();

        const conversationRequestDto = ApiClient.buildConversationRequestDto({
            conversationId: conversationId,
            messages: messages,
            displayedCategories: displayedProductCategories,
            cartProductVariantInternalId: cartProductVariantInternalId,
            pageContents: pageContents
        });

        ApiClient.sendMessage(
            merchant.id,
            conversationRequestDto,
            (searchResultResponseDto) => {
                searchResultResponseDto.showExpandableProducts = true;
                handleReceivedProductCollections(searchResultResponseDto);
            },
            handleMessageChunkReceived,
            handleOnComplete,
            handleError,
            handleProcessingStatusUpdate
        );
    }

    const sendSearchRequest = () => {
        const searchDto = ApiClient.buildSearchRequestDto({
            conversationId: conversationId,
            searchQuery: messages[messages.length - 1].content,
            limit: merchant.searchResultsToShow || Constants.SearchLimit,
            offset: 0, // Offset is always 0 for conversations, it's only used for loading more products in the ProductGrid
            structuredFilters: null  // This method is only executed when we first enter the chatbot and only when we come in from a search bar, so we don't have any structured filters yet
        });

        ApiClient.searchProducts(
            merchant.id,
            searchDto,
            handleReceivedProductCollections,
            handleMessageChunkReceived,
            handleOnComplete,
            handleError,
            handleProcessingStatusUpdate
        );
    }

    const handleProcessingStatusUpdate = (status) => {
        setCurrentStatus(status);
    };

    function replaceStatusMessageWithAssistantResponse(prevMessages, lastAssistantIndex, productCategories, areAdditionalProductsAvailable, showExpandableProducts) {
        const index = prevMessages.length - 1 - lastAssistantIndex;
        const updatedMessages = [...prevMessages];
        const lastAssistantMessage = updatedMessages[index];
        updatedMessages[index] = {
            ...lastAssistantMessage,
            productCategories: productCategories,
            areAdditionalProductsAvailable: areAdditionalProductsAvailable,
            showExpandableProducts: showExpandableProducts,
        };
        return updatedMessages;
    }

    function createNewMessageForLatestAssistantResponse(productCategories, areAdditionalProductsAvailable, prevMessages, showExpandableProducts) {
        const assistantMessage = ToMessage("", AssistantRole);
        assistantMessage.productCategories = productCategories;
        assistantMessage.areAdditionalProductsAvailable = areAdditionalProductsAvailable;
        assistantMessage.showExpandableProducts = showExpandableProducts;
        return [...prevMessages, assistantMessage];
    }

    function updateMessageListWithResponseFromAssistant(productCategories, areAdditionalProductsAvailable, showExpandableProducts) {
        setMessages(prevMessages => {
            // Find the last assistant message
            const lastAssistantIndex = [...prevMessages].reverse().findIndex(message => message.role === AssistantRole);
            let updatedMessage;
            if (lastAssistantIndex !== -1) {
                return replaceStatusMessageWithAssistantResponse(prevMessages, lastAssistantIndex, productCategories, areAdditionalProductsAvailable, showExpandableProducts);
            }
            else {
                // No assistant message yet, create one
                return createNewMessageForLatestAssistantResponse(productCategories, areAdditionalProductsAvailable, prevMessages, showExpandableProducts);
            }
        });
    }

    const handleReceivedProductCollections = (searchResultResponseDto) => {
        let productCategories = searchResultResponseDto.categories;
        setLastStructuredFilterPromptResponse(searchResultResponseDto.structuredFilters);
        updateMessageListWithResponseFromAssistant(productCategories, searchResultResponseDto.areAdditionalProductsAvailable, searchResultResponseDto.showExpandableProducts);

        // Use a timeout to allow the UI to update and then scroll the product grid into view, but only after the first search (since the first message back is at the top of the window and the user can see them come through)
        if (displayedProductCategories && displayedProductCategories.length > 0) {
            setTimeout(() => {
                if (productGridRef.current) {
                    productGridRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, 100);
        }

        setDisplayedProductCategories(productCategories);
    }

    const scrollToLastMessage = () => {
        if (lastMessageRef.current && lastMessageRef.current.scrollIntoView) {
            lastMessageRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }

    const handleError = (error) => {
        console.error(error);
        setDisableMessageSending(false);
        setWaitingForFirstToken(false);
        setMessages(prev => [
            ...prev,
            {role: AssistantRole, content: "I'm sorry, I encountered an error. Please try again."}
        ]);
    }

    const handleOnComplete = () => {
        setDisableMessageSending(false);
        setWaitingForFirstToken(false);
    }

    function replaceMessageWithLatestChunk(prevMessages, lastAssistantIndex, messageChunk) {
        // Update the existing assistant message
        const index = prevMessages.length - 1 - lastAssistantIndex;
        const updatedMessages = [...prevMessages];
        const lastAssistantMessage = updatedMessages[index];
        let updatedMessage = filterOutCodeBlocks(lastAssistantMessage.content + messageChunk);  // Sometimes the tool calls get specified within the text content as code blocks, so we need to remove them

        updatedMessages[index] = {
            ...lastAssistantMessage,
            content: updatedMessage
        };
        return updatedMessages;
    }

    function filterOutCodeBlocks(message) {
        // Remove code blocks from the message
        const codeBlockRegex = /```[\s\S]*?```/g;
        return message.replace(codeBlockRegex, "");
    }

    /**
     * Handle a message chunk received from the API
     *
     * @param message {string} - The message chunk received
     */
    const handleMessageChunkReceived = (messageChunk) => {
        setWaitingForFirstToken(false); // Stop the status updates
        setMessages(prevMessages => {
            // Find the last assistant message
            const lastAssistantIndex = [...prevMessages].reverse().findIndex(message => message.role === AssistantRole);
            if (lastAssistantIndex !== -1) {
                return replaceMessageWithLatestChunk(prevMessages, lastAssistantIndex, messageChunk);
            }
            else {
                // No assistant message yet, create one
                return [...prevMessages, ToMessage(messageChunk, AssistantRole)];
            }
        });
    };

    /**
     * This method is the callback for the MessageForm and constructs a chat message and then adds it to the conversation, triggering the use effect to
     * send the message to the API
     *
     * @param messageContent {string} The message content
     */
    function sendMessage(messageContent) {
        if (!messageContent) {
            return;
        }

        // For chatbots embedded into the cart, we don't have conversations directly in that chatbot because of the constrained space, so we'll instead pass the messages up to the App, which will load them up into a new chatbot where the conversation can take place
        if (entryPoint && entryPoint === EntryPoint.Cart && onSendMessage && initialAssistantMessage) {
            onSendMessage(initialAssistantMessage, messageContent);
            return;
        }

        const chatMessage = ToMessage(messageContent, UserRole);
        setMessages(prevMessages => [...prevMessages, chatMessage]);
    }

    const prepareNewConversation = (newConversationId) => {
        if (!newConversationId) {
            newConversationId = uuidv4();
        }

        let defaultMessage = ToMessage(NewSearchPrompt, AssistantRole);
        ApiClient.getBestSellers(
            merchant.id,
            (searchResultResponseDto) => {
                if (searchResultResponseDto.categories && searchResultResponseDto.categories.length > 0) {
                    defaultMessage.productCategories = searchResultResponseDto.categories;
                    defaultMessage.showSearchWithoutAiButton = false;
                    defaultMessage.showExpandableProducts = true;
                    defaultMessage.areAdditionalProductsAvailable = false;
                    setMessages([defaultMessage]);
                }
            },
            (error) => {
                console.error("Error fetching best sellers:", error);
                defaultMessage = ToMessage(NewSearchPrompt, AssistantRole);
            }
        );

        setMessages([defaultMessage]);
        setDisableMessageSending(false);
        setConversationId(newConversationId);
    }

    //////////////////////////////////////////////////
    // Render
    //////////////////////////////////////////////////

    function renderMessageContent(isAssistant, markdownContent) {
        const messageType = isAssistant ? AssistantRole : UserRole;
        let safeMarkdown = markdownContent;
        // if it *starts* with a list marker, prefix a newline so remarkGfm will parse it:
        if (/^(\s*[-*+] |\s*\d+\.\s+)/.test(safeMarkdown)) {
            safeMarkdown = "\n" + safeMarkdown;
        }

        // Only show modified styles for the cart when on desktop
        return <div className={`${styles.messageContentContainer} ${styles[messageType]} ${entryPoint === EntryPoint.Cart ? styles.cartMessage : ""}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                {safeMarkdown}
            </ReactMarkdown>
        </div>;
    }

    function renderProductsGrid(message, index) {
        const isLatestGrid =
            index === messages.length - 1 ||
            // Alternatively, check if there’s no later message with products
            !messages.slice(index + 1).some((m) => m.productCategories);

        return <ProductsGrid
            productCategories={message.productCategories}
            merchant={merchant}
            brandTheme={brandTheme}
            conversationId={conversationId}
            query={messages[0].content}
            structuredFilterPromptResponse={lastStructuredFilterPromptResponse}
            productGridRef={isLatestGrid ? productGridRef : null}
            entryPoint={entryPoint}
            assistantConfiguration={assistantConfiguration}
            areAdditionalProductsAvailable={message.areAdditionalProductsAvailable}
            showExpandableProducts={message.showExpandableProducts}
        />;
    }

    function renderSearchWithoutAiButton(message) {
        let searchUrl = "/search?q=" + encodeURIComponent(message.content);

        return <Button
            variant={"text"}
            endIcon={<OpenInNewIcon style={{fontSize: "14px", marginTop: "-3px"}} />}
            className={styles.searchWithoutAiButton}
            style={{
                color: brandTheme?.general?.buttons?.backgroundColor
            }}
            onClick={() => {
                NobiEventTracker.trackEvent(
                    merchant.id,
                    entryPoint, Events.ClickSearchWithoutAiButton,
                    false,
                    true,
                    null,
                    { query: message.content });
                window.open(searchUrl, '_blank');
            }}>
            Search Without AI
        </Button>
    }

    /**
     * This method renders a message in the chat interface
     *
     * @param message {object} The message object
     * @param index {number} The index of the message in the conversation
     * @returns {Element} The message element
     */
    function renderMessage(message, index) {
        const markdownContent = message.content;
        const isAssistant = message.role === AssistantRole;
        const modifiedMessageStyles = { justifyContent: isAssistant ? "flex-start" : "flex-end" };
        const isMessageMostRecentAssistantMessage = index === messages.length - 1 && isAssistant;
        let isLastMessage = index === messages.length - 1;

        return (
            <Box key={index} data-testid={`message-${index}`} style={modifiedMessageStyles} className={styles.messageContainer}>
                {isAssistant && (!entryPoint || entryPoint !== EntryPoint.Cart) && renderAssistantChatIcon()}
                <Box className={`${styles.messageContentAndProductsContainer} ${entryPoint === EntryPoint.Cart ? styles.noGap : ""}`}>
                    {
                        isAssistant && !markdownContent && waitingForFirstToken && isMessageMostRecentAssistantMessage
                            ? renderThinkingAnimation()
                            : null
                    }
                    {
                        !isAssistant || !waitingForFirstToken || !isMessageMostRecentAssistantMessage || markdownContent === NewSearchPrompt
                            ? renderMessageContent(isAssistant, markdownContent, message.showSearchWithoutAiButton)
                            : null
                    }
                    {
                        message && message.showSearchWithoutAiButton
                            ? renderSearchWithoutAiButton(message)
                            : null
                    }
                    {
                        message && message.productCategories
                            ? renderProductsGrid(message, index)
                            : null
                    }
                </Box>
            </Box>
        )
    }

    const renderAssistantChatIcon = () => {
        return <Box className={styles.logoContainer}
                    style={{ marginTop: "0.25rem", background: brandTheme?.general?.buttons?.backgroundColor + ChatIconOpacityAsHex }}>
            <img
                src={brandTheme?.general?.logo?.url}
                alt="Logo"
            />
        </Box>
    }

    function renderMessages() {
        return <Box className={`${styles.messagesContainer} ${entryPoint == EntryPoint.Cart ? styles.cartMessagesContainer : ""}`} key="messagesContainer">
            {messages.map((message, index) => renderMessage(message, index))}
            <div ref={lastMessageRef} />
        </Box>
    }

    const renderThinkingAnimation = () => {
        return <div id={styles.thinkingAnimationContainer} className={styles.statusContainer}>
            <span className={styles.statusMessage}>
                {currentStatus}
            </span>
            <ThinkingIndicator brandTheme={brandTheme} />
        </div>
    }

    function renderChatHeader() {
        return (
            <Box className={styles.chatboxHeader} style={entryPoint === EntryPoint.Cart ? { padding: "15px" } : {}}>
                <Box className={`${styles.flexRow} ${styles.withGap}`}>
                    <Box className={styles.logoContainer} style={{ background: brandTheme?.general?.buttons?.backgroundColor + ChatIconOpacityAsHex }}>
                        <img
                            src={brandTheme?.general?.logo?.url}
                            alt="Logo"
                        />
                    </Box>
                    <Typography variant="p" className={styles.chatbotTitle}>
                        AI Shopping Assistant
                    </Typography>
                </Box>
            </Box>
        )
    }

    function getPlaceholder() {
        if (entryPoint === EntryPoint.RespondingFromCartAssistant || entryPoint === EntryPoint.Cart) {
            return "Have any questions?";
        }

        let isNewSearch = messages.length === 0 || (messages.length === 1 && messages[0].role === AssistantRole);
        if (isNewSearch && assistantConfiguration?.startingPlaceholder) {
            return assistantConfiguration.startingPlaceholder;
        }

        return Constants.MessageFormPlaceholderForFiltering;
    }

    return (
        <div id={styles.chatbotAndMessageFormContainer} className={entryPoint === EntryPoint.Cart ? styles.chatbotAndMessageFormContainerCartStylesForCart : null} data-testid="chatbotAndMessageFormContainer">
            <Paper
                id={styles.chatbot}
                data-testid={"chatbot"}
                className={entryPoint === EntryPoint.Cart ? styles.chatbotCartStyles : ""}
                elevation={entryPoint === EntryPoint.Cart ? 0 : 8}>
                {renderMessages()}
            </Paper>
            {(showMessageForm === null || showMessageForm === undefined || showMessageForm) &&
                <Box id={styles.userInputContainer} className={entryPoint === EntryPoint.Cart ? styles.userInputContainerCartStyles : ""}>
                    <MessageForm
                        handleSubmit={sendMessage}
                        disableMessages={disableMessageSending}
                        brandTheme={brandTheme}
                        key={conversationId}
                        merchant={merchant}
                        entryPoint={entryPoint}
                        showSuggestions={entryPoint !== EntryPoint.Cart && entryPoint !== EntryPoint.RespondingFromCartAssistant}
                        conversationId={conversationId}
                        placeholder={getPlaceholder()}
                        assistantConfiguration={assistantConfiguration}
                    />
                </Box>
            }
        </div>
    );
}

Chatbot.propTypes = {
    merchant: PropTypes.object.isRequired,
    initialUserMessage: PropTypes.string,
    initialAssistantMessage: PropTypes.string,
    brandTheme: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    entryPoint: PropTypes.string,
    onSendMessage: PropTypes.func,
    initialCategories: PropTypes.array,
    assistantConfiguration: PropTypes.object,
    cartProductVariantInternalId: PropTypes.string,
    showMessageForm: PropTypes.bool,
    showCommentary: PropTypes.bool
}